import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable} from 'rxjs';
import { Subject } from 'rxjs/Subject';
import { UserService } from '../../services/user.service';
import { takeUntil,distinctUntilKeyChanged } from 'rxjs/operators';

import { IPatient, IPatientName, IPatientPhoto } from '../../interfaces/patient.interface';
import { Ipractitioner } from '../../interfaces/practitioner.interface';
import { usersTypes } from '../../constants/usersTypes.enum';
import {ArrayUtils} from "@ispm/shared/utils/Array.utils";

@Component({
  selector: 'gen-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileComponent implements OnInit, OnDestroy {
  @Input() userType: usersTypes;
  @Input() id?: string;
  @Input() name?: string;
  @Input() picUrl?: string = '';
  @Input() layout?: string;
  @Input() date?: string;
  @Input() showInitials: boolean;
  @Input() showPrefix: boolean;
  @Input() showSuffix: boolean;
  @Input() backgroundColor?: string = '';

  public className: string;

  private componentSubscriber$: Subject<any> = new Subject();
  private destroyed: boolean = false;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private userService: UserService,
  ) {
  }

  ngOnInit(): void {
    this.setClassName();

    if (!(this.name || this.picUrl) && this.id) {
      this.fetchUserData();
    }
  }

  ngOnDestroy(): void {
    this.componentSubscriber$.next();
    this.componentSubscriber$.complete();
    this.destroyed = true;
  }

  setClassName() {
    this.className = profileLayouts[this.layout] || 'PIC_AND_NAME';
  }

  fetchUserData() {
    const userResource: Observable<IPatient | Ipractitioner> = this.userType === usersTypes.PATIENT
      ? this.userService.getPatient(this.id)
      : this.userService.getPractitioner(this.id);

    userResource
      .pipe(
        ArrayUtils.filterEmpty(),
        distinctUntilKeyChanged("id"),
        takeUntil(this.componentSubscriber$.asObservable()),
      )
      .subscribe((user: IPatient|Ipractitioner) => {
        if(user) {
          this.setUserData(user.name, user.photo);

          if (!this.destroyed) {
            this.changeDetector.detectChanges();
          }
        }
      });
  }

  setUserData(name: Array<IPatientName>, picUrl: Array<IPatientPhoto>) {
    this.name = '';

    if (this.showPrefix && Array.isArray(name[0].prefix) && name[0].prefix.length > 0) {
      this.name += name[0].prefix.join(' ') + '. ';
    }

    this.name += name[0].text;

    if (this.showSuffix && Array.isArray(name[0].suffix) && name[0].suffix.length > 0) {
      this.name += ' ' + name[0].suffix.join(' ');
    }

    if (this.showInitials && name[0].given[0].length > 0 && name[0].family[0].length > 0) {
      this.name = `${name[0].given[0][0]}${name[0].family[0][0]}`;
    }

    if (typeof picUrl !== 'undefined' && picUrl !== null && picUrl.length > 0) {
      this.picUrl = picUrl[0].url;
    }
  }

  shouldShown(elementName: string) {
    switch (elementName) {
      case 'avatar':
        return [
          profileLayouts.PIC_AND_NAME,
          profileLayouts.PIC_ONLY,
          profileLayouts.SMALL_PIC_AND_NAME,
          profileLayouts.USER_WITH_DATE_ONE_LINE,
          profileLayouts.USER_WITH_DATE_TWO_LINES,
        ].includes(this.className);
      case 'name':
        return [
          profileLayouts.NAME_AND_DATE,
          profileLayouts.NAME_ONLY,
          profileLayouts.PIC_AND_NAME,
          profileLayouts.SMALL_PIC_AND_NAME,
          profileLayouts.USER_WITH_DATE_ONE_LINE,
          profileLayouts.USER_WITH_DATE_TWO_LINES,
        ].includes(this.className);
      case 'date':
        return [
          profileLayouts.NAME_AND_DATE,
          profileLayouts.USER_WITH_DATE_ONE_LINE,
          profileLayouts.USER_WITH_DATE_TWO_LINES,
        ].includes(this.className);
      case 'initials':
        return [
          profileLayouts.INITIALS_ONLY,
        ].includes(this.className);
    }
  }
}

export const profileLayouts = {
  'INITIALS_ONLY': 'INITIALS_ONLY',
  'NAME_AND_DATE': 'NAME_AND_DATE',
  'NAME_ONLY': 'NAME_ONLY',
  'PIC_AND_NAME': 'PIC_AND_NAME',
  'PIC_ONLY': 'PIC_ONLY',
  'SMALL_PIC_AND_NAME': 'SMALL_PIC_AND_NAME',
  'USER_WITH_DATE_ONE_LINE': 'USER_WITH_DATE_ONE_LINE',
  'USER_WITH_DATE_TWO_LINES': 'USER_WITH_DATE_TWO_LINES',
};
